import { getCurrencySymbol } from "@/pages/deals/helpers";
// Intl-backed, unlike the currency-list getCurrencySymbol above: this is the
// sign formatCurrency itself prints, which is what the override replaces.
import { getCurrencySymbol as getIntlCurrencySymbol } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import {
  useAccountSettingByKey,
  useCurrencyRates
} from "@/entities/settings/hooks";
import {
  currencyCodeFromSetting,
  distinguishCurrencySign,
  isCurrencyConversionNeeded
} from "@/utils/numbers";
import { DEFAULT_CURRENCY_CODE } from "@/utils/numberFormat";

/**
 * The account's own currency — the basis every financial figure is normalized
 * to. Read on its own by callers that only need to name the currency (a column
 * header, a total) and have no source currency to convert from.
 *
 * Null while the setting is still loading and when the read fails. Callers
 * that name the currency to the user must keep that distinct from a confirmed
 * value: standing in a default would label a GBP account's figures as USD.
 */
export const useAccountCurrency = (): string | null => {
  const accountCurrencySetting =
    useAccountSettingByKey("account_currency").data;
  return currencyCodeFromSetting(accountCurrencySetting?.value);
};

export interface AccountCurrencyResolution {
  accountCurrency: string | null;
  /**
   * True only once the setting has actually loaded successfully — not merely
   * once loading has stopped. A fetch error also stops loading, and retry is
   * disabled for non-auth errors (`createAuthRetryHandler`), so gating on
   * `!isLoading` alone would call a failed read "resolved" too. A caller that
   * snapshots a currency-code header into persisted column state (Columns
   * management, a saved view) must wait for a genuine success before seeding
   * — otherwise the snapshot freezes the bare, no-currency label permanently,
   * unlike a re-rendered grid header which is never snapshotted and so
   * self-corrects once a later refetch succeeds.
   */
  isResolved: boolean;
  /**
   * The read is still in flight. Distinct from `isResolved`: a failed read is
   * never pending but never resolves either, because retry is disabled for
   * non-auth errors. Callers gating an *action* must wait on this rather than
   * on `!isResolved`, which would disable the action permanently on a failed
   * read; degrading to no currency is recoverable, an unusable form is not.
   */
  isPending: boolean;
}

export const useAccountCurrencyResolution = (): AccountCurrencyResolution => {
  const { data, isSuccess, isLoading } =
    useAccountSettingByKey("account_currency");
  return {
    accountCurrency: currencyCodeFromSetting(data?.value),
    isResolved: isSuccess,
    isPending: isLoading
  };
};

export const useCurrencyConversion = (currency: string | null | undefined) => {
  // Conversion needs a base to compute against, so an unresolved account
  // currency falls back here rather than in the shared hook — `showConversion`
  // below still gates whether the result is shown.
  const resolvedAccountCurrency = useAccountCurrency();
  const accountCurrency = resolvedAccountCurrency ?? DEFAULT_CURRENCY_CODE;
  const accountCurrencySymbol = getCurrencySymbol(accountCurrency) ?? "";
  // An unknown source currency can't be converted — computing a rate against
  // a guessed base (e.g. defaulting to USD) would silently show a fabricated
  // converted amount rather than none at all. The account side is held to the
  // same standard: while the setting is still loading it is unknown, not
  // "USD", so gate on the resolved value. Defaulting here showed a converted
  // figure and then withdrew it once the account turned out to be the
  // record's own currency.
  // Shared with useFinancials and DealTopFields rather than re-derived: the
  // rule has to stay in step with distinguishCurrencySign's own case-folding,
  // and hand-rolled copies of it have already drifted apart once.
  const showConversion = isCurrencyConversionNeeded(
    resolvedAccountCurrency,
    currency
  );
  const rates = useCurrencyRates(currency || DEFAULT_CURRENCY_CODE)?.data;
  const rate = currency ? rates?.rates[accountCurrency] : undefined;
  // For the surfaces that print `<record figure> (<account figure>)` with
  // formatCurrency on the record half. Seeded from formatCurrency's own
  // resolver, not the one behind accountCurrencySymbol: the sign compared has
  // to be the sign the surface actually prints, and seeding the other one
  // replaced every record sign with its currency-list spelling (an INR
  // contract rendering the Bengali `টকা` in place of `₹`).
  const recordCurrencySignOverride = currency
    ? distinguishCurrencySign({
        sign: getIntlCurrencySymbol(currency),
        currencyCode: currency,
        accountSign: accountCurrencySymbol,
        accountCurrencyCode: resolvedAccountCurrency
      })
    : undefined;

  return {
    accountCurrency,
    accountCurrencySymbol,
    recordCurrencySignOverride,
    showConversion,
    rate
  };
};
